import React, { useEffect, useState } from "react";
import { Divider } from "antd";
import PlanPurchasePageHeader from "./components/PlanPurchasePageHeader";
import ProgressSteps from "./components/ProgressSteps";
import PlanListing from "./components/PlanListing/PlanListing";
import AddonListing from "./components/AddonListing/AddonListing";
import Payment from "./components/Payment/Payment";
import cartService, { Cart } from "../../Service/cart.service";
import { useSearchParams } from "react-router-dom";
import { getStepsForMode, StepKey } from "./types";
import useAccess from "../../Hooks/useAccess";
import { isAnnualMode } from "../../Utils/generic-functions";
import { useLoader } from "../../Context/LoaderContext";
import { Subscription } from "../../Entities/Subscription";
import "./index.scss";

const PlanPurchase: React.FC = () => {
  const { currentPlan, currentSubscription } = useAccess();
  const { setLoading, setLoadingMessage } = useLoader();

  const [searchParams] = useSearchParams();
  const selectedAddonId = searchParams.get("addonId");
  const action = searchParams.get("action");

  const [currentStep, setCurrentStep] = useState<StepKey>("plan");
  const [userCart, setUserCart] = useState<Cart>(cartService.getCartData());
  const [mode, setMode] = useState<"upgrade" | "addon_only" | "renew">(
    "upgrade",
  );

  const handleStepChange = (step: StepKey) => {
    setCurrentStep(step);
  };

  const getContentByActiveStep = () => {
    switch (currentStep) {
      case "plan":
        return (
          <PlanListing
            userCart={userCart}
            handleStepChange={handleStepChange}
            activePlan={currentPlan}
            currentSubscription={currentSubscription}
          />
        );
      case "addons":
        return (
          <AddonListing
            userCart={userCart}
            handleStepChange={handleStepChange}
            activePlan={currentPlan}
            currentSubscription={currentSubscription}
            mode={mode}
            selectedAddonId={selectedAddonId}
          />
        );
      case "payment":
        return (
          <Payment
            mode={mode}
            userCart={userCart}
            selectedAddonId={selectedAddonId}
          />
        );
      default:
        return (
          <PlanListing
            userCart={userCart}
            handleStepChange={handleStepChange}
            activePlan={currentPlan}
            currentSubscription={currentSubscription}
          />
        );
    }
  };

  const handleRenewPlan = (userCurrentSubscription: Subscription) => {
    const isAnnualBillingPeriod = isAnnualMode(
      userCurrentSubscription.startDate,
      userCurrentSubscription.endDate,
    );

    const basePlanId = userCurrentSubscription.planId;
    const billingPeriod = isAnnualBillingPeriod ? "year" : "month";

    cartService.setSelectedBasePlan(basePlanId);
    cartService.setBillingPeriod(billingPeriod === "year");

    setCurrentStep("payment");
  };

  const init = async (
    userAction: string,
    currentSubscription: Subscription,
    userSelectedAddonId: string | null,
  ) => {
    try {
      setLoading(true);
      setLoadingMessage("Loading. Please wait...");

      if (userAction === "upgrade") {
        setMode("upgrade");
      } else if (userAction === "addon" && userSelectedAddonId) {
        setMode("addon_only");
      } else if (userAction === "renew") {
        setMode("renew");
        handleRenewPlan(currentSubscription);
      } else {
        setMode("upgrade");
      }
      return;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  useEffect(() => {
    if (action && currentSubscription) {
      init(action, currentSubscription, selectedAddonId || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, currentSubscription, selectedAddonId]);

  // Effect to handle cart changes
  useEffect(() => {
    const handleCartChange = () => {
      setUserCart(cartService.getCartData());
    };

    cartService.subscribe("CART_CHANGED", handleCartChange);

    return () => {
      cartService.unsubscribe("CART_CHANGED", handleCartChange);
    };
  }, []);

  // Effect to handle mode changes
  useEffect(() => {
    const steps = getStepsForMode(mode);
    setCurrentStep((prev) => (steps.includes(prev) ? prev : steps[0]));
  }, [mode]);

  return (
    <section className="dashboard-page plan-purchase-page hide-scroll-bar">
      <PlanPurchasePageHeader mode={mode} />
      <Divider style={{ margin: 0 }} />
      {mode && mode !== "renew" && (
        <ProgressSteps
          currentStep={currentStep}
          handleStepChange={handleStepChange}
          userCart={userCart}
          mode={mode}
        />
      )}
      <div className="plan-purchase-page-content-wrapper">
        {getContentByActiveStep()}
      </div>
    </section>
  );
};

export default PlanPurchase;
